import { Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent, merge } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import {
  AdminTab,
  AriadneContext,
  NameTabsOfStation,
  StationPageSection,
  StationQueryParams,
} from 'src/models';
import { BoardService } from '../board/board.service';
import { ContainerService } from './container.service';
import { SidenavDrawerService } from './sidenav-drawer.service';
import { StationService } from './station.service';
import { UrlNodeSyncService } from './url-node-sync.service';
import { UserService } from './user.service';

/** Matches a Rithm id / GUID-like segment in a route. */
const RITHM_ID_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Custom window event fired when the History API is written to programmatically. */
const LOCATION_CHANGE_EVENT = 'ariadne:locationchange';

/** Maps the first route segment to a friendly area label. */
const AREA_LABELS: Record<string, string> = {
  '': 'Sign in',
  boards: 'Board',
  board: 'Board',
  station: 'Station',
  container: 'Container',
  map: 'Map',
  admin: 'Admin',
  settings: 'Settings',
  search: 'Search',
  print: 'Print preview',
};

/** Admin `?tab=` value → friendly label (values are lowercased AdminTab enum values). */
const ADMIN_TAB_LABELS: Record<string, string> = {
  [AdminTab.Members.toLowerCase()]: 'Members',
  [AdminTab.Teams.toLowerCase()]: 'Teams',
  [AdminTab.Libraries.toLowerCase()]: 'Libraries',
  [AdminTab.Aliases.toLowerCase()]: 'Aliases',
  [AdminTab.Integrations.toLowerCase()]: 'Integrations',
  [AdminTab.FlowedBy.toLowerCase()]: 'Flowed By',
  [AdminTab.AccountSetting.toLowerCase()]: 'Account Settings',
};

/** Station `?tab=` value → friendly label. */
const STATION_TAB_LABELS: Record<string, string> = {
  [NameTabsOfStation.Grid]: 'Components',
  [NameTabsOfStation.Rules]: 'Flow Logic',
  [NameTabsOfStation.Issues]: 'Issues',
  [NameTabsOfStation.Members]: 'Members',
  [NameTabsOfStation.Containers]: 'Containers',
  [NameTabsOfStation.Trash]: 'Trash',
  [NameTabsOfStation.History]: 'History',
  [NameTabsOfStation.Settings]: 'Settings',
};

/** Station Flow Logic section → friendly suffix. */
const STATION_SECTION_LABELS: Record<string, string> = {
  [StationPageSection.ConditionSection]: 'Condition',
  [StationPageSection.TriggerSection]: 'Trigger',
  [StationPageSection.ActionSection]: 'Action',
};

/**
 * Aggregates "where the user is" into a single live {@link AriadneContext} signal for the Ariadne
 * assistant. It enriches the route-level view with the active sub-section/tab, the open detail
 * panel, and the primary entity in view — the structured locator the future AI/MCP service needs.
 *
 * Detection seam: sub-tabs (Station, Admin) are written to the URL with `Location.go()` /
 * `replaceState()`, which fire neither `NavigationEnd` nor `popstate`. So this service patches the
 * History API once to emit a synthetic event, and also subscribes to the relevant root state
 * services (station params, drawer context) whose changes are not reflected in the URL at all.
 */
@Injectable({
  providedIn: 'root',
})
export class AriadneContextService {
  /** The enriched, live context. Read this signal to know exactly where the user is. */
  readonly context = signal<AriadneContext>(this.emptyContext());

  /** Latest station query params (tab / rule section), cached from the BehaviorSubject. */
  private latestStationParams?: StationQueryParams;

  /** Latest open drawer/panel context, cached from the ReplaySubject. */
  private latestDrawerContext = '';

  /** Guards the one-time History API patch. */
  private static historyPatched = false;

  constructor(
    private router: Router,
    private urlSync: UrlNodeSyncService,
    private stationService: StationService,
    private containerService: ContainerService,
    private boardService: BoardService,
    private sidenavDrawerService: SidenavDrawerService,
    private userService: UserService,
  ) {
    this.patchHistory();

    // Cache the non-URL state sources and recompute when they change.
    this.stationService.stationQueryParams$.subscribe((params) => {
      this.latestStationParams = params;
      this.recompute();
    });
    this.sidenavDrawerService.drawerContext$.subscribe((ctx) => {
      this.latestDrawerContext = ctx ?? '';
      this.recompute();
    });

    // Recompute on every kind of URL change: Router navigation, programmatic History writes
    // (Location.go/replaceState) and browser back/forward.
    merge(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
      fromEvent(window, LOCATION_CHANGE_EVENT),
      fromEvent(window, 'popstate'),
    )
      .pipe(debounceTime(50))
      .subscribe(() => this.recompute());

    this.recompute();
  }

  /**
   * Patches `history.pushState` / `history.replaceState` to dispatch a synthetic event so
   * programmatic URL changes (which bypass the Angular Router) can be observed. Runs once.
   */
  private patchHistory(): void {
    if (AriadneContextService.historyPatched || typeof history === 'undefined') {
      return;
    }
    AriadneContextService.historyPatched = true;

    const fire = (): void => {
      window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
    };

    const wrap = (
      original: History['pushState'],
    ): History['pushState'] =>
      function patched(this: History, ...args) {
        const result = original.apply(this, args);
        fire();
        return result;
      };

    history.pushState = wrap(history.pushState);
    history.replaceState = wrap(history.replaceState);
  }

  /** Reads all sources and rebuilds the context signal. */
  private recompute(): void {
    const route = this.router.url || '/';
    const segments = route.split('?')[0].split('/').filter(Boolean);
    const areaKey = (segments[0] ?? '').toLowerCase();
    const entityIds = route.match(RITHM_ID_REGEX) ?? [];

    const base: AriadneContext = {
      route,
      host: window.location.host,
      url: window.location.href,
      areaKey: areaKey || 'unknown',
      areaLabel: AREA_LABELS[areaKey] ?? 'Rithm',
      entityIds,
      pageTitle: document.title,
      queryParams: this.readQueryParams(),
    };

    this.context.set({ ...base, ...this.resolveArea(areaKey, segments) });
  }

  /** Reads the current query string into a plain record. */
  private readQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }

  /**
   * Per-area resolver: fills the sub-section, detail panel and primary entity for the given area.
   * Adding a new area is a matter of adding a branch here.
   * @param areaKey The first route segment (lowercased).
   * @param segments All non-empty route segments.
   * @returns The area-specific slice of the context.
   */
  private resolveArea(
    areaKey: string,
    segments: string[],
  ): Partial<AriadneContext> {
    switch (areaKey) {
      case 'admin':
        return this.resolveAdmin();
      case 'station':
        return this.resolveStation();
      case 'boards':
      case 'board':
        return this.resolveBoard();
      case 'container':
        return this.resolveContainer();
      case 'settings':
        return this.resolveSettings(segments);
      default:
        return {};
    }
  }

  /** Admin: sub-section from the `tab` query param; entity is the organization. */
  private resolveAdmin(): Partial<AriadneContext> {
    const tab = this.urlSync.readParam('tab') ?? undefined;
    const org = this.userService.user().organization;
    return {
      subSectionKey: tab,
      subSectionLabel: tab ? (ADMIN_TAB_LABELS[tab] ?? tab) : undefined,
      entity: org ? { type: 'organization', id: org } : undefined,
    };
  }

  /** Station: tab (+ Flow Logic section) from station params; entity is the current station/group. */
  private resolveStation(): Partial<AriadneContext> {
    const tab =
      this.latestStationParams?.tab ??
      (this.urlSync.readParam('tab') as NameTabsOfStation | null) ??
      undefined;
    const section = this.latestStationParams?.section;

    let label = tab ? (STATION_TAB_LABELS[tab] ?? tab) : undefined;
    if (tab === NameTabsOfStation.Rules && section) {
      label = `${label} › ${STATION_SECTION_LABELS[section] ?? section}`;
    }

    const option = this.stationService.currentStationOption$.getValue();
    const entity = option.rithmId
      ? { type: option.type, id: option.rithmId, name: option.name }
      : undefined;

    return {
      subSectionKey: tab,
      subSectionLabel: label,
      detailPanel: this.latestDrawerContext || undefined,
      entity,
    };
  }

  /** Board: edit/selected-widget state from query params; entity is the active board. */
  private resolveBoard(): Partial<AriadneContext> {
    const editMode = this.urlSync.readParam('editMode') === 'true';
    const selectedWidget = this.urlSync.readParam('selectedWidget');
    const subSectionKey = editMode
      ? 'edit'
      : selectedWidget
        ? 'widget-selected'
        : undefined;
    const subSectionLabel = editMode
      ? 'Edit mode'
      : selectedWidget
        ? 'Widget selected'
        : undefined;

    const boardId = this.boardService.currentActiveBoardId$.getValue();
    return {
      subSectionKey,
      subSectionLabel,
      detailPanel: this.latestDrawerContext || undefined,
      entity: boardId ? { type: 'board', id: boardId } : undefined,
    };
  }

  /** Container: the open drawer is the sub-section; entity is the current container. */
  private resolveContainer(): Partial<AriadneContext> {
    const panel = this.latestDrawerContext || undefined;
    const name = this.containerService.containerName$.getValue();
    const displayName = `${name.baseName}${name.appendedName}`.trim();
    const containerId = this.urlSync.readParam('containerId') ?? '';

    return {
      subSectionKey: panel,
      subSectionLabel: panel,
      detailPanel: panel,
      entity: containerId
        ? {
            type: 'container',
            id: containerId,
            name: displayName || undefined,
          }
        : undefined,
    };
  }

  /** Settings: the child path segment is the sub-section (e.g. "account"). */
  private resolveSettings(segments: string[]): Partial<AriadneContext> {
    const key = segments[1];
    if (!key) {
      return {};
    }
    const label = key.charAt(0).toUpperCase() + key.slice(1);
    return { subSectionKey: key, subSectionLabel: label };
  }

  /** A minimal context used for the initial signal value before the first recompute. */
  private emptyContext(): AriadneContext {
    return {
      route: '/',
      host: window.location.host,
      url: window.location.href,
      areaKey: 'unknown',
      areaLabel: 'Rithm',
      entityIds: [],
      pageTitle: document.title,
      queryParams: {},
    };
  }
}
